import { createContext, type ReactNode, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import { type SettingsSection, settingsHref } from "~/common/settings/catalog";

/**
 * The way in to settings, from anywhere inside the shell.
 *
 * Settings is a page under a brand — `/w/{brandId}/settings/{section}` —
 * because half of it is about one. A caller inside a brand opens that brand's.
 * A caller outside every brand — the claim page, the wizard, a refusal raised
 * before a brand list loaded — opens the account's first brand's, because the
 * person and the plan read the same from any of them. An account with no brand
 * yet opens the page above the brand, which drops the brand's own sections.
 */
const BRAND = /^\/w\/([^/]+)/;

const OpenSettings = createContext<(section?: SettingsSection) => void>(() => {});

/** Opens the settings page, from anywhere inside the shell. */
export function useAccount(): (section?: SettingsSection) => void {
	return useContext(OpenSettings);
}

export function AccountProvider({ firstBrand, children }: { firstBrand?: string; children: ReactNode }) {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	// Decoded, because the address bar holds it encoded and the path builder
	// encodes it again.
	const inPath = BRAND.exec(pathname)?.[1];
	const brand = inPath ? decodeURIComponent(inPath) : firstBrand;

	const open = useCallback(
		(section: SettingsSection = "profile") => navigate(settingsHref(brand ?? "", section)),
		[brand, navigate],
	);

	return <OpenSettings.Provider value={open}>{children}</OpenSettings.Provider>;
}
